<template>
  <div v-if="isActive" class="modal-popup" @click="handleOverlayClick">
    <div class="popup two-factor-popup" @click.stop>
      <!-- Success state -->
      <div v-if="connected" class="two-factor-success">
        <div class="success-icon">✓</div>
        <h4>{{ $t('onboarding.2fa.success_title') || 'Connexion réussie !' }}</h4>
        <p v-if="analysts && analysts.length" class="analysts-warning">
          {{ $t('dash.bots.analysts') }} {{ analysts.join(', ') }}
        </p>
        <div class="buttons">
          <div class="button btn-primary" @click="closeSuccess">
            {{ $t('onboarding.2fa.continue') || 'Continuer' }}
          </div>
        </div>
      </div>

      <!-- Awaiting push notification / in-app approval state (Polling + waiting animation + step guide) -->
      <div v-else-if="page && isPushApproval" class="two-factor-awaiting-approval">
        <div class="page-header">
          <div class="page-avatar">
            <img v-if="page.avatar" :src="page.avatar" :alt="page.name" />
            <div v-else class="page-avatar-placeholder">
              {{ page.name ? page.name[0].toUpperCase() : '?' }}
            </div>
          </div>
          <h4>{{ page.name }}</h4>
        </div>

        <!-- Phone Pulse & Waiting Animation -->
        <div class="push-approval-animation">
          <div class="device-icon-pulse">
            <svg class="phone-svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
              <line x1="12" y1="18" x2="12.01" y2="18"></line>
            </svg>
            <div class="pulse-ring"></div>
          </div>
          <div class="spinner-circle"></div>
        </div>

        <div class="two-factor-instructions push-instructions">
          <p v-if="denied" class="denied-text">{{ $t('onboarding.2fa.approval_denied') }}</p>
          <div v-else class="push-steps">
            <h5 class="push-title">{{ $t('onboarding.2fa.push_waiting_title') || 'Approbation requise sur Instagram' }}</h5>
            <div class="push-step">
              <span class="step-badge">1</span>
              <span>{{ $t('onboarding.2fa.push_step_1') || 'Ouvrez l\'application Instagram sur votre téléphone' }}</span>
            </div>
            <div class="push-step">
              <span class="step-badge">2</span>
              <span>
                {{ $t('onboarding.2fa.push_step_2') || 'Appuyez sur la notification et confirmez l\'appareil :' }}
                <strong v-if="deviceName" class="device-badge">{{ deviceName }}</strong>
              </span>
            </div>
            <div class="push-detecting">
              <span class="pulse-dot"></span>
              <span>{{ $t('onboarding.2fa.push_detecting') || 'En attente de votre validation sur mobile...' }}</span>
            </div>
          </div>
        </div>

        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <p v-if="infoMessage" class="info-message">{{ infoMessage }}</p>

        <div class="buttons">
          <div v-if="denied" class="button btn-outline-primary" @click="cancel">
            {{ $t('onboarding.2fa.ok') }}
          </div>
          <template v-else>
            <div class="button btn-outline-primary" @click="cancel">
              {{ $t('onboarding.2fa.cancel') }}
            </div>
            <div
              class="button btn-primary"
              :class="{ 'loading': isSubmitting }"
              @click="submitCode"
            >
              {{ isSubmitting ? ($t('onboarding.2fa.connecting') || 'Vérification...') : ($t('onboarding.2fa.push_manual_btn') || 'J\'ai validé sur mon téléphone') }}
            </div>
          </template>
        </div>
      </div>

      <!-- 2FA form (Code-based verification methods) -->
      <div v-else-if="page && twoFactorInfo" class="two-factor-form">
        <!-- Page info -->
        <div class="page-header">
          <div class="page-avatar">
            <img v-if="page.avatar" :src="page.avatar" :alt="page.name" />
            <div v-else class="page-avatar-placeholder">
              {{ page.name ? page.name[0].toUpperCase() : '?' }}
            </div>
          </div>
          <h4>{{ page.name }}</h4>
        </div>

        <!-- Instructions based on verification method -->
        <div class="two-factor-instructions">
          <p v-if="twoFactorInfo.verification_method == 0">
            {{ $t('onboarding.2fa.method_app') || 'Veuillez renseigner ci-dessous le code de vérification reçu sur l\'application vous permettant d\'effectuer vos authentifications à deux facteurs :' }}
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 1">
            {{ $t('onboarding.2fa.method_sms') || 'Veuillez renseigner ci-dessous le code de vérification reçu par SMS :' }}
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 90">
            {{ $t('onboarding.2fa.method_email_sms') || 'Veuillez entrer le code reçu par Email ou SMS :' }}
            <br>
            <small class="method-note">
              {{ $t('onboarding.2fa.method_experimental') || '(Cette méthode de vérification est en cours de test et peut rencontrer des difficultés)' }}
            </small>
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 99">
            {{ $t('onboarding.2fa.method_push_or_sms') || 'Veuillez accepter l\'appareil depuis la notification Instagram ou entrer le code reçu par SMS :' }}
            <br>
            <small class="method-note">
              {{ $t('onboarding.2fa.method_leave_empty') || '(Si vous recevez une notification, laissez le champ vide)' }}
            </small>
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 98">
            {{ $t('onboarding.2fa.method_linkedin') || 'Veuillez entrer le code reçu par Email ou SMS :' }}
          </p>
        </div>

        <!-- Code input -->
        <div class="two-factor-input">
          <input
            v-model="verificationCode"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            class="code-input"
            :placeholder="$t('onboarding.2fa.code_placeholder') || 'Code de vérification'"
            @keypress.enter="submitCode"
            autofocus
          />
        </div>

        <!-- Error message -->
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        
        <!-- Info message -->
        <p v-if="infoMessage" class="info-message">{{ infoMessage }}</p>

        <!-- Buttons -->
        <div class="buttons">
          <div class="button btn-outline-primary" @click="cancel">
            {{ $t('onboarding.2fa.cancel') || 'Annuler' }}
          </div>
          <div
            class="button btn-primary"
            :class="{ 'loading': isSubmitting }"
            :disabled="isSubmitDisabled"
            @click="submitCode"
          >
            {{ isSubmitting ? $t('onboarding.2fa.connecting') || 'Connexion...' : $t('onboarding.2fa.connect') || 'Connecter' }}
          </div>
        </div>

        <!-- Resend code (LinkedIn only) -->
        <div v-if="twoFactorInfo.verification_method == 98" class="resend-section">
          <p class="resend-text">{{ $t('dash.pages.linkedin_code') }}</p>
          <button class="resend-button" @click="resendCode">
            {{ $t('dash.pages.linkedin_code_btn') || 'Renvoyer le code' }}
          </button>
        </div>
      </div>

      <!-- Error state -->
      <div v-else class="two-factor-error">
        <h4>{{ $t('onboarding.2fa.error_title') || 'Erreur' }}</h4>
        <p>{{ $t('onboarding.2fa.error_no_page') || 'Une erreur s\'est produite, veuillez réessayer.' }}</p>
        <div class="buttons">
          <div class="button btn-primary" @click="cancel">
            {{ $t('onboarding.2fa.close') || 'Fermer' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Bots from '../../mixins/Bots.vue'

export default {
  name: 'TwoFactorModal',

  mixins: [Bots],

  props: {
    active: {
      type: Boolean,
      default: false
    },
    page: {
      type: Object,
      default: null
    },
    createPage: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      verificationCode: '',
      errorMessage: '',
      infoMessage: '',
      connected: false,
      analysts: [],
      denied: false,
      isSubmitting: false,
      pollIntervalId: null,
      pollStartedAt: null
    }
  },

  computed: {
    isActive() {
      return this.active
    },

    storyConfig() {
      if (!this.page) return null
      let cfg = this.page.story_config
      if (typeof cfg === 'string') {
        try {
          cfg = JSON.parse(cfg)
        } catch (e) {
          console.warn('[TwoFactorModal] Failed to parse story_config string:', e)
        }
      }
      return cfg || null
    },

    awaitingApproval() {
      if (!this.storyConfig) return false

      const { awaitingApproval, two_factor } = this.storyConfig
      return !!(awaitingApproval && !two_factor)
    },

    twoFactorInfo() {
      if (!this.storyConfig) {
        if (this.page && this.page.twoAuth) {
          return { verification_method: 0 }
        }
        return null
      }
      
      const { two_factor, checkpoint, challenge_informations, challenge } = this.storyConfig
      
      if (two_factor) {
        if (typeof two_factor === 'object') {
          return two_factor
        }
        try {
          return JSON.parse(two_factor)
        } catch (e) {
          console.error('[TwoFactorModal] Failed to parse two_factor:', e)
          return { verification_method: 0 }
        }
      }
      
      if (checkpoint) {
        return { verification_method: 99 }
      }
      
      if (challenge_informations || challenge) {
        return { verification_method: 98 }
      }

      if (this.page && this.page.twoAuth) {
        return { verification_method: 0 }
      }
      
      return null
    },

    isPushApproval() {
      if (!this.storyConfig) return false
      if (this.awaitingApproval) return true
      const info = this.twoFactorInfo
      return !!(info && (info.verification_method == 4 || info.verification_method === '4'))
    },

    deviceName() {
      if (this.storyConfig && this.storyConfig.user_agent) {
        const parts = this.storyConfig.user_agent.split(';')
        if (parts.length > 4) {
          return parts[4].trim()
        }
      }
      return 'sm-g980f'
    },

    isSubmitDisabled() {
      if (this.isSubmitting) return true
      
      // Push notification (method 4) or push approval doesn't require code
      if (this.isPushApproval) return false
      const method = this.twoFactorInfo ? Number(this.twoFactorInfo.verification_method) : null
      // Checkpoint method 99: can be submitted without code if user approved on device
      if (method === 4 || method === 99) return false
      
      // LinkedIn challenge (method 98) requires at least 1 char
      if (method === 98) {
        return !this.verificationCode || String(this.verificationCode).trim().length === 0
      }
      
      // Other methods require 6-digit code
      const codeStr = String(this.verificationCode || '').trim()
      return codeStr.length < 6
    }
  },

  watch: {
    verificationCode(val) {
      if (typeof val === 'string') {
        const isLinkedInChallenge = this.twoFactorInfo && Number(this.twoFactorInfo.verification_method) === 98
        if (!isLinkedInChallenge) {
          // Keep only digits, stripping spaces or dashes (e.g. "123 456" -> "123456")
          const clean = val.replace(/\D/g, '').slice(0, 6)
          if (clean !== val) {
            this.verificationCode = clean
          }
        } else {
          // LinkedIn challenge: trim whitespace, limit length
          const clean = val.trim().slice(0, 12)
          if (clean !== val) {
            this.verificationCode = clean
          }
        }
      }
    },

    active(newVal) {
      if (newVal) {
        // Reset state when modal opens
        this.verificationCode = ''
        this.errorMessage = ''
        this.infoMessage = ''
        this.connected = false
        this.analysts = []
        if (this.isPushApproval) {
          this.startPolling()
        }
      } else {
        this.stopPolling()
      }
    }
  },

  beforeDestroy() {
    this.stopPolling()
  },

  methods: {
    handleOverlayClick() {
      // Only allow closing if not submitting
      if (!this.isSubmitting) {
        this.cancel()
      }
    },

    cancel() {
      if (this.isSubmitting) return

      this.stopPolling()
      this.$emit('cancel')
      this.$emit('update:active', false)
    },

    startPolling() {
      this.stopPolling()
      this.denied = false
      this.errorMessage = ''
      this.pollStartedAt = Date.now()
      this.pollApproval()
      this.pollIntervalId = setInterval(() => {
        this.pollApproval()
      }, 4000)
    },

    stopPolling() {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId)
        this.pollIntervalId = null
      }
    },

    async pollApproval() {
      // Give up after a few minutes if the other device never responds.
      if (Date.now() - this.pollStartedAt > 3 * 60 * 1000) {
        this.stopPolling()
        this.errorMessage = this.$t('onboarding.2fa.approval_timeout') || "Le délai d'approbation a expiré, veuillez réessayer."
        return
      }

      // A tick landing while a check is still running joins it instead of starting a new one
      if (this.approvalRequest) return

      try {
        this.applyApprovalResponse(await this.checkApproval(), { manual: false })
      } catch (error) {
        console.error('[TwoFactorModal] Error checking approval status:', error)
      }
    },

    // Single-flight approval check shared by the poll and the "J'ai validé" button.
    // Completing an approval takes longer than the poll interval - a second concurrent
    // request would replay the already-used approval and report an error.
    checkApproval() {
      if (this.approvalRequest) return this.approvalRequest

      const request = this.awaitingApproval
        ? this.checkTwoAuthApproval({ pageId: this.page.id })
        : this.twoFactorLinkedinConnect({
            verificationCode: '',
            pageId: this.page.id,
            createPage: this.createPage
          })

      this.approvalRequest = request.finally(() => {
        this.approvalRequest = null
      })
      return this.approvalRequest
    },

    applyApprovalResponse(response, { manual }) {
      // A late response must never cover the success state
      if (this.connected) return

      const body = response && response.ok ? response.body : null

      if (body && body.connected) {
        this.stopPolling()
        this.errorMessage = ''
        this.infoMessage = ''
        this.connected = true
        this.analysts = body.analysts || []
        setTimeout(() => {
          this.$emit('connected')
        }, 1500)
      } else if (body && body.denied) {
        this.stopPolling()
        this.infoMessage = ''
        this.denied = true
      } else if (body && body.pending) {
        if (manual) {
          this.infoMessage = this.$t('onboarding.2fa.push_not_yet') || 'Validation pas encore détectée, réessayez dans quelques secondes.'
        }
      } else if (manual) {
        this.errorMessage = `${response.status}: ${response.body?.[0]?.msg || response.bodyText || 'Erreur de connexion'}`
      }
    },

    closeSuccess() {
      this.$emit('success')
      this.$emit('update:active', false)
    },

    async submitCode() {
      if (this.isSubmitDisabled) return
      
      this.errorMessage = ''
      this.infoMessage = ''
      this.isSubmitting = true

      // Push approval has no code to submit: check the approval itself, joining any
      // poll already in flight (the code endpoint has no two_factor to work with here)
      if (this.isPushApproval) {
        try {
          this.applyApprovalResponse(await this.checkApproval(), { manual: true })
        } catch (error) {
          console.error('[TwoFactorModal] Error checking approval status:', error)
          this.errorMessage = this.$t('onboarding.2fa.error_network') || 'Erreur réseau'
        } finally {
          this.isSubmitting = false
        }
        return
      }

      try {
        const method = this.twoFactorInfo ? this.twoFactorInfo.verification_method : null
        
        const cleanCode = String(this.verificationCode || '').trim()

        console.log('[TwoFactorModal] Submitting 2FA code:', {
          network: this.page.network,
          method: method,
          pageId: this.page.id,
          accountId: this.page.account,
          codeLength: cleanCode.length
        })
        
        // Determine which API to call based on verification method and network
        let response
        
        if (this.page.network === 'instagram' || [4, 99].includes(method) || this.isPushApproval) {
          console.log('[TwoFactorModal] Calling twoFactorLinkedinConnect for Instagram 2FA...')
          response = await this.twoFactorLinkedinConnect({
            verificationCode: cleanCode,
            pageId: this.page.id,
            createPage: this.createPage
          })
          
          console.log('[TwoFactorModal] Instagram 2FA response:', {
            ok: response.ok,
            status: response.status,
            body: response.body,
            bodyConnected: response.body?.connected
          })
          
          if (response.ok && response.body && response.body.connected) {
            console.log('[TwoFactorModal] Instagram 2FA success!')
            this.stopPolling()
            this.connected = true
            this.analysts = response.body.analysts || []
            
            // Emit success after short delay
            setTimeout(() => {
              this.$emit('connected')
            }, 1500)
          } else if (response.ok && response.body && response.body.badPin) {
            console.error('[TwoFactorModal] Instagram 2FA failed - bad PIN')
            this.errorMessage = this.$t('dash.pages.code_enter_incorrect') || 'Code incorrect'
          } else {
            console.error('[TwoFactorModal] Instagram 2FA failed - response:', response.body)
            this.errorMessage = `${response.status}: ${response.body?.[0]?.msg || response.bodyText || 'Erreur de connexion'}`
          }
        } else {
          // LinkedIn 2FA or other networks
          response = await this.twoFactorLinkedinConnect({
            verificationCode: cleanCode,
            pageId: this.page.id,
            createPage: this.createPage
          })
          
          if (response.ok && response.body && response.body.connected) {
            this.stopPolling()
            this.connected = true
            this.analysts = response.body.analysts || []
            
            // Emit success after short delay
            setTimeout(() => {
              this.$emit('connected')
            }, 1500)
          } else if (response.ok && response.body && response.body.badPin) {
            this.errorMessage = this.$t('dash.pages.code_enter_incorrect') || 'Code incorrect'
          } else {
            this.errorMessage = `${response.status}: ${response.body?.[0]?.msg || response.bodyText || 'Erreur de connexion'}`
          }
        }
      } catch (error) {
        console.error('[TwoFactorModal] Error submitting code:', error)
        this.errorMessage = this.$t('onboarding.2fa.error_network') || 'Erreur réseau'
      } finally {
        this.isSubmitting = false
      }
    },

    async resendCode() {
      this.errorMessage = ''
      this.infoMessage = ''
      
      try {
        const response = await this.twoFactorLinkedinResendCode({
          pageId: this.page.id
        })
        
        if (response.ok && response.body && response.body.success && response.body.resend) {
          this.infoMessage = this.$t('dash.pages.linkedin_code_success') || 'Code renvoyé avec succès'
        } else {
          this.errorMessage = `${response.status}: ${response.body?.[0]?.msg || response.bodyText || 'Erreur lors du renvoi'}`
        }
      } catch (error) {
        console.error('[TwoFactorModal] Error resending code:', error)
        this.errorMessage = this.$t('onboarding.2fa.error_resend') || 'Impossible de renvoyer le code'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

.modal-popup {
  z-index: 10000 !important;
}

// Use global .modal-popup and .popup styles
// Only define 2FA-specific styles here

.two-factor-popup {
  max-width: 480px;
}

.two-factor-success {
  text-align: center;
  padding: 20px 0;

  .success-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 20px;
    background: #10b981;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
    font-weight: bold;
  }

  h4 {
    color: #111827;
    margin-bottom: 12px;
  }

  .analysts-warning {
    color: #f59e0b;
    font-size: 14px;
    margin-top: 16px;
    margin-bottom: 0;
  }
}

.two-factor-form,
.two-factor-awaiting-approval {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .page-header {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-bottom: 20px;

    .page-avatar {
      width: 56px;
      height: 56px;
      margin: 0 auto 10px;
      border-radius: 50%;
      overflow: hidden;
      background: $bg-ui-selected;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &-placeholder {
        font-size: 24px;
        font-weight: 700;
        color: $primary-color;
      }
    }

    h4 {
      width: 100%;
      margin: 0;
      color: #111827;
      font-size: 18px;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      line-height: 1.4;
    }
  }

  .push-approval-animation {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 4px 0 18px;
    height: 72px;

    .device-icon-pulse {
      position: relative;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: $bg-ui-selected;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $primary-color;
      z-index: 2;

      .phone-svg {
        animation: phone-bounce 2s ease-in-out infinite;
      }

      .pulse-ring {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 2px solid $primary-color;
        animation: pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
        opacity: 0.6;
      }
    }

    .spinner-circle {
      position: absolute;
      width: 64px;
      height: 64px;
      border: 2px dashed rgba($primary-color, 0.35);
      border-radius: 50%;
      animation: two-factor-approval-spin 6s linear infinite;
    }
  }

  .two-factor-instructions {
    width: 100%;
    box-sizing: border-box;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      color: #374151;
      text-align: center;
    }

    strong {
      color: #111827;
      font-weight: 600;
    }

    .method-note {
      display: block;
      margin-top: 8px;
      font-size: 12px;
      color: #6b7280;
      font-style: italic;
    }

    &.push-instructions {
      .push-steps {
        display: flex;
        flex-direction: column;
        gap: 12px;
        text-align: left;

        .push-title {
          font-size: 14.5px;
          font-weight: 700;
          color: #111827;
          margin: 0 0 2px 0;
          text-align: center;
        }

        .push-step {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 13.5px;
          line-height: 1.4;
          color: #374151;

          .step-badge {
            flex-shrink: 0;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: $primary-color;
            color: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: 700;
          }

          .device-badge {
            display: inline-block;
            margin-left: 4px;
            padding: 2px 7px;
            background: #e5e7eb;
            color: #111827;
            border-radius: 6px;
            font-family: monospace;
            font-size: 12px;
            letter-spacing: 0.5px;
          }
        }

        .push-detecting {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 4px;
          padding-top: 10px;
          border-top: 1px dashed #e5e7eb;
          font-size: 12.5px;
          color: #6b7280;
          font-style: italic;

          .pulse-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #10b981;
            animation: dot-pulse 1.5s ease-in-out infinite;
          }
        }
      }
    }
  }

  .two-factor-input {
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 16px;

    .code-input {
      width: 100%;
      box-sizing: border-box;
      padding: 12px 16px;
      font-size: 16px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      text-align: center;
      letter-spacing: 4px;
      font-weight: 500;
      transition: all 0.2s ease;

      &:focus {
        outline: none;
        border-color: $primary-color;
        box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
      }

      &::placeholder {
        letter-spacing: normal;
        color: #9ca3af;
      }

      // Remove arrows from number input
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &[type=number] {
        -moz-appearance: textfield;
      }
    }
  }

  .error-message {
    width: 100%;
    box-sizing: border-box;
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    text-align: center;
  }

  .info-message {
    width: 100%;
    box-sizing: border-box;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    text-align: center;
  }

  .buttons {
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 12px;

    .button {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      cursor: pointer;
    }
  }

  .resend-section {
    width: 100%;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
    text-align: center;

    .resend-text {
      font-size: 13px;
      color: #6b7280;
      margin-bottom: 8px;
    }

    .resend-button {
      background: transparent;
      border: none;
      color: $primary-color;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      padding: 8px 16px;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background: $bg-ui-selected;
      }
    }
  }
}

.two-factor-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0 10px;

  h4 {
    color: #dc2626;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 12px;
    text-align: center;
    width: 100%;
  }

  p {
    color: #6b7280;
    font-size: 14px;
    line-height: 1.5;
    text-align: center;
    width: 100%;
    margin-bottom: 24px;
  }

  .buttons {
    width: 100%;
    display: flex;
    justify-content: center;

    .button {
      width: 100%;
    }
  }
}

.button {
  &.loading {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

@keyframes two-factor-approval-spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse-ring {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.35); opacity: 0; }
  100% { transform: scale(0.95); opacity: 0; }
}

@keyframes phone-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.75); }
}
</style>
